import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useRecaptcha } from '../hooks/useRecaptcha';

interface LoginProps {
  open: boolean;
  onClose: () => void;
}

const Login: React.FC<LoginProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { executeRecaptcha } = useRecaptcha();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage(t('validation.emailRequired'));
      setMessageType('error');
      return;
    }

    setIsLoading(true);
    setMessage('');
    setMessageType(null);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('login');
      
      const result = await login(email, recaptchaToken);
      
      if (result.success) {
        setMessage(result.message);
        setMessageType('success');
        setIsSuccess(true);
        setCountdown(5);
        // Don't close the dialog immediately, let user see the success message
      } else {
        setMessage(result.message);
        setMessageType('error');
      }
    } catch (error) {
      setMessage(t('auth.loginError'));
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    setEmail('');
    setMessage('');
    setMessageType(null);
    setIsSuccess(false);
    setCountdown(0);
    onClose();
  }, [onClose]);

  // Auto-close timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && isSuccess) {
      handleClose();
    }
    return () => clearTimeout(timer);
  }, [countdown, isSuccess, handleClose]);

  const handleRegisterClick = () => {
    handleClose();
    window.location.href = '/';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('auth.login')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('auth.emailLabel')}
              placeholder={t('auth.emailPlaceholder')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              disabled={isLoading}
              required
            />
            
            {message && (
              <Alert severity={messageType || 'info'}>
                {message}
                {isSuccess && countdown > 0 && (
                  <Box sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
                    {t(countdown === 1 ? 'auth.autoCloseCountdown_one' : 'auth.autoCloseCountdown_other', { count: countdown })}
                  </Box>
                )}
                {messageType === 'error' && message.includes('register') && (
                  <Box sx={{ mt: 1 }}>
                    <Link
                      component="button"
                      type="button"
                      onClick={handleRegisterClick}
                      sx={{ textDecoration: 'underline' }}
                    >
                      {t('auth.registerNow')}
                    </Link>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading || isSuccess}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !email.trim() || isSuccess}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? t('messages.loading') : t('auth.sendMagicLink')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default Login;